import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";
import { Page, PageHeader, Panel, SearchInput } from "~/components/ui/page";
import { chapterSource, chapterTitle, guideSections, searchGuide } from "~/features/help/guide";
import { HELP_CHAPTERS } from "~/features/help/registry";
import { useLocale, useT } from "~/lib/i18n";

export const Route = createFileRoute("/_app/help/")({ component: HelpHome });

/** The guide: one chapter per workflow, and a search across every section. */
function HelpHome() {
  const t = useT();
  const [locale] = useLocale();
  const [query, setQuery] = useState("");
  const sections = useMemo(() => guideSections(locale), [locale]);
  const hits = useMemo(() => searchGuide(sections, query), [sections, query]);
  return (
    <Page className="max-w-4xl">
      <PageHeader title={t("help.ui.title")} />
      <p className="-mt-2 mb-5 text-sm text-fg-2">{t("help.ui.subtitle")}</p>
      <Panel>
        <div className="border-b border-line p-3">
          <SearchInput value={query} onChange={setQuery} placeholder={t("help.ui.search")} />
        </div>
        {query.trim() ? (
          hits.length === 0 ? (
            <p className="px-5 py-6 text-sm text-fg-3">{t("help.ui.noResult")}</p>
          ) : (
            <ul className="divide-y divide-line">
              {hits.map((hit) => (
                <li key={`${hit.chapter}-${hit.id}`}>
                  <Link
                    to="/help/$chapter"
                    params={{ chapter: hit.chapter }}
                    hash={hit.id}
                    className="flex items-center justify-between gap-3 px-5 py-3 text-sm hover:bg-sunken"
                  >
                    <span>
                      <span className="block font-medium text-fg">{hit.title}</span>
                      <span className="block text-xs text-fg-3">{hit.chapterTitle}</span>
                    </span>
                    <ChevronRight className="size-4 shrink-0 text-fg-3" aria-hidden />
                  </Link>
                </li>
              ))}
            </ul>
          )
        ) : (
          <ul className="divide-y divide-line">
            {HELP_CHAPTERS.map((chapter) => (
              <li key={chapter}>
                <Link
                  to="/help/$chapter"
                  params={{ chapter }}
                  className="flex items-center gap-3 px-5 py-3.5 text-sm font-medium text-fg hover:bg-sunken"
                >
                  <BookOpen className="size-4 shrink-0 text-fg-3" aria-hidden />
                  <span className="flex-1">{chapterTitle(chapterSource(locale, chapter))}</span>
                  <ChevronRight className="size-4 shrink-0 text-fg-3" aria-hidden />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </Page>
  );
}
